import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { LoadingService } from './loading.service';
import { ToastService } from './toast.service';
import { environment as env } from '../../environments/environment';

// Manual bulk Welcome Email client — the Send Welcome Email item under
// the Users → Actions menu. Two calls, both admin-gated at the
// backend:
//   preview  → routes each user, returns A/B/skipped counts + a
//              rendered sample per variant using the first user's data
//   send     → routes + sends. Bypasses commerce_settings.welcome_
//              email_enabled per-customer gate (admin clicked = intent).

export interface WelcomeEmailUserInput {
  email: string;
  // Cognito username — usually equals email but can diverge on pools
  // that seeded users with a UUID username. Passed through to the
  // modal so the Skipped tab's "Copy magic link" button can call
  // POST /rest/admin/users/:username/mint-magic-link without another
  // grid round-trip. IT-F2-<pending> (2026-07-30).
  username?: string;
  pool_id?: string;
  customer_slug: string;
  scanner_ids?: string[];
}

export interface WelcomeEmailPreviewGroup {
  count: number;
  sample_subject?: string;
  sample_html?: string;
  sample_text?: string;
  sample_from?: string;
  sample_reply_to?: string;
  users: Array<{ email: string; customer_slug: string; scanner_labels: string[]; scanner_url?: string }>;
}

export interface WelcomeEmailPreview {
  ok: boolean;
  a: WelcomeEmailPreviewGroup;
  b: WelcomeEmailPreviewGroup;
  skipped: { count: number; users: Array<{ email: string; customer_slug: string; reason: string }> };
}

export interface WelcomeEmailSendResult {
  ok: boolean;
  sent: number;
  failed: number;
  skipped: number;
  results: Array<{ email: string; customer_slug: string; mode: 'magic_link' | 'activation' | 'skipped' | 'failed'; reason?: string }>;
}

@Injectable({ providedIn: 'root' })
export class WelcomeEmailService {
  constructor(
    private http: HttpClient,
    private loadingService: LoadingService,
    private toast: ToastService,
  ) {}

  private _opts() { return { withCredentials: true }; }

  async preview(users: WelcomeEmailUserInput[]): Promise<WelcomeEmailPreview | null> {
    const id = this.loadingService.add(`Rendering welcome-email preview (${users.length})`);
    const url = `${env.apiUrl}/rest/welcome-email/preview`;
    try {
      const res: any = await this.http.post(url, { users }, this._opts()).toPromise();
      return res as WelcomeEmailPreview;
    } catch (e: any) {
      this._toastErr('Welcome-email preview', e, 'F2A_WELCOME_EMAIL_PREVIEW_FAIL', url);
      return null;
    } finally {
      this.loadingService.remove(id);
    }
  }

  async send(users: WelcomeEmailUserInput[]): Promise<WelcomeEmailSendResult | null> {
    const id = this.loadingService.add(`Sending welcome emails (${users.length})`);
    const url = `${env.apiUrl}/rest/welcome-email/send`;
    try {
      const res: any = await this.http.post(url, { users }, this._opts()).toPromise();
      return res as WelcomeEmailSendResult;
    } catch (e: any) {
      this._toastErr('Welcome-email send', e, 'F2A_WELCOME_EMAIL_SEND_FAIL', url);
      return null;
    } finally {
      this.loadingService.remove(id);
    }
  }

  private _toastErr(title: string, e: any, code: string, requestUrl?: string) {
    const msg = e?.error?.errors?.[0]?.message || e?.message || 'Unknown error';
    this.toast.error(title, msg, {
      error_code: code,
      customer: 'f2-admin',
      scanner_path: '/admin/users/welcome-email',
      request_url: requestUrl || e?.url || undefined,
      http_status: e?.status ?? 0,
      content_type: (e?.headers?.get?.('content-type') as string | undefined) || (e?.error && typeof e.error === 'string' ? 'text/html' : 'application/json'),
      f2_response_body: (typeof e?.error === 'string' ? e.error : JSON.stringify(e?.error || {})).slice(0, 500),
      f2_response_message: msg,
    });
  }
}
